// src/components/admin/Catatan.jsx
import React, { useState, useMemo, useEffect, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { 
  XMarkIcon, MagnifyingGlassIcon, BookmarkIcon as PinSolidIcon, 
  PencilIcon
} from "@heroicons/react/24/solid";
import { BookmarkIcon as PinOutlineIcon } from "@heroicons/react/24/outline";

import { rtdb } from "../../config/firebase";
import { ref, push, update, remove, onValue } from "firebase/database";

// Konfigurasi warna catatan
const noteColors = {
  gray: { bg: "bg-gray-100", border: "border-gray-200" },
  yellow: { bg: "bg-yellow-100", border: "border-yellow-200" },
  green: { bg: "bg-green-100", border: "border-green-200" },
  blue: { bg: "bg-blue-100", border: "border-blue-200" },
};

// ======= NOTE CARD =======
const NoteCard = ({ note, onPin, onDelete, onEdit }) => {
  return (
    <motion.div
      layout
      initial={{ opacity: 0, scale: 0.9 }}
      animate={{ opacity: 1, scale: 1 }}
      exit={{ opacity: 0, scale: 0.8 }}
      transition={{ duration: 0.3 }}
      className={`group relative ${noteColors[note.color].bg} border ${noteColors[note.color].border} rounded-xl shadow-sm flex flex-col hover:shadow-xl transition-shadow duration-300`}
    >
      <div className="p-4 flex-grow flex flex-col">
        {note.title && <h3 className="font-bold text-gray-800 mb-2 flex-shrink-0">{note.title}</h3>}
        <p className="text-sm text-gray-700 break-words flex-grow whitespace-pre-wrap">{note.content}</p>
      </div>

      <div className="flex justify-between items-center p-4 pt-2 border-t border-black/5">
        <span className="text-xs text-gray-500">{new Date(note.createdAt).toLocaleDateString("id-ID")}</span>
        <div className="flex items-center opacity-0 group-hover:opacity-100 transition-opacity duration-200">
          <button onClick={() => onPin(note.id)} className="p-1.5 text-gray-500 hover:text-green-600" title="Sematkan">
            {note.isPinned ? <PinSolidIcon className="w-4 h-4" /> : <PinOutlineIcon className="w-4 h-4" />}
          </button>
          <button onClick={() => onEdit(note)} className="p-1.5 text-gray-500 hover:text-blue-600" title="Edit">
            <PencilIcon className="w-4 h-4" />
          </button>
          <button onClick={() => onDelete(note.id)} className="p-1.5 text-gray-500 hover:text-red-600" title="Hapus">
            <XMarkIcon className="w-4 h-4" />
          </button>
        </div>
      </div>
    </motion.div>
  );
};

// ======= MAIN COMPONENT =======
export default function Catatan() {
  const [notes, setNotes] = useState([]);
  const [inputTitle, setInputTitle] = useState("");
  const [inputContent, setInputContent] = useState("");
  const [newNoteColor, setNewNoteColor] = useState("gray");
  const [isComposerExpanded, setIsComposerExpanded] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [editingNoteId, setEditingNoteId] = useState(null);

  const composerRef = useRef(null);

  // ===== Load notes from Firebase =====
  useEffect(() => {
    const notesRef = ref(rtdb, "notes");
    const unsubscribe = onValue(notesRef, (snapshot) => {
      const data = snapshot.val();
      setNotes(data ? Object.keys(data).map(id => ({ id, ...data[id] })) : []);
    });
    return () => unsubscribe();
  }, []);

  // ===== Handle outside click composer =====
  useEffect(() => {
    function handleClickOutside(event) {
      if (composerRef.current && !composerRef.current.contains(event.target)) {
        setIsComposerExpanded(false);
        if (!editingNoteId) clearComposer();
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [editingNoteId]);

  const clearComposer = () => {
    setInputTitle("");
    setInputContent("");
    setNewNoteColor("gray");
    setEditingNoteId(null);
  };

  const handleSaveNote = async (e) => {
    e.preventDefault();
    if (inputTitle.trim() === "" && inputContent.trim() === "") return;

    if (editingNoteId) {
      await update(ref(rtdb, `notes/${editingNoteId}`), {
        title: inputTitle,
        content: inputContent,
        color: newNoteColor,
      });
    } else {
      await push(ref(rtdb, "notes"), {
        title: inputTitle,
        content: inputContent,
        color: newNoteColor,
        isPinned: false,
        createdAt: Date.now(),
      });
    }

    clearComposer();
    setIsComposerExpanded(false);
  };

  const handleEdit = (note) => {
    setEditingNoteId(note.id);
    setInputTitle(note.title);
    setInputContent(note.content);
    setNewNoteColor(note.color);
    setIsComposerExpanded(true);
  };

  const handleDeleteNote = async (id) => {
    if (window.confirm("Hapus catatan ini?")) {
      await remove(ref(rtdb, `notes/${id}`));
    }
  };

  const handleTogglePin = async (id) => {
    const note = notes.find(n => n.id === id);
    if (!note) return;
    await update(ref(rtdb, `notes/${id}`), { isPinned: !note.isPinned });
  };

  const filteredAndSortedNotes = useMemo(() => {
    return notes
      .filter(note => 
        note.title.toLowerCase().includes(searchTerm.toLowerCase()) || 
        note.content.toLowerCase().includes(searchTerm.toLowerCase())
      )
      .sort((a, b) => b.isPinned - a.isPinned || b.createdAt - a.createdAt);
  }, [notes, searchTerm]);

  return (
    <div className="flex flex-col h-full p-4 md:p-6 bg-slate-50">
      {/* HEADER */}
      <motion.header
        initial={{ y: -100, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.5 }}
        className="sticky top-0 z-20 bg-white/70 backdrop-blur-xl rounded-2xl shadow-lg border border-white/50 mb-6 p-4"
      >
        <div className="flex flex-wrap justify-between items-center gap-4">
          <h2 className="text-2xl font-bold text-gray-800">
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-green-600 to-teal-500">
              Papan
            </span> Catatan
          </h2>
          <div className="relative w-full max-w-xs">
            <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
              <MagnifyingGlassIcon className="w-5 h-5 text-gray-400" />
            </div>
            <input 
              type="text" 
              placeholder="Cari catatan..." 
              value={searchTerm} 
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-3 py-2 text-sm border border-gray-300 rounded-lg bg-white/50 focus:outline-none focus:ring-2 focus:ring-green-500"
            />
          </div>
        </div>
      </motion.header>

      {/* COMPOSER */}
      <motion.div 
        ref={composerRef} 
        layout 
        initial={{ y: -20, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ delay: 0.2, duration: 0.4 }}
        className="bg-white/60 backdrop-blur-xl rounded-2xl shadow-md border border-white/50 mb-8"
      >
        <form onSubmit={handleSaveNote}>
          <AnimatePresence>
            {isComposerExpanded && (
              <motion.input
                initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
                type="text" placeholder="Judul..." value={inputTitle} onChange={(e) => setInputTitle(e.target.value)}
                className="w-full font-semibold border-b border-white/50 focus:ring-0 p-4 bg-transparent placeholder-gray-500"
              />
            )}
          </AnimatePresence>
          <textarea
            onFocus={() => setIsComposerExpanded(true)}
            value={inputContent} onChange={(e) => setInputContent(e.target.value)}
            placeholder="Tulis catatan..." rows={isComposerExpanded ? 3 : 1}
            className={`w-full border-none focus:ring-0 resize-none p-4 bg-transparent placeholder-gray-500 transition-all duration-300 ${isComposerExpanded ? 'text-sm' : 'font-medium'}`}
          />
          <AnimatePresence>
            {isComposerExpanded && (
              <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="flex justify-between items-center p-4 pt-0 border-t border-white/50">
                <div className="flex items-center gap-2">
                  {Object.keys(noteColors).map(color => (
                    <button key={color} type="button" onClick={() => setNewNoteColor(color)}
                      className={`w-6 h-6 rounded-full ${noteColors[color].bg} border-2 transition ${newNoteColor === color ? 'border-green-500 ring-2 ring-green-200' : 'border-transparent hover:border-gray-400'}`}
                    />
                  ))}
                </div>
                <div className="flex items-center gap-2">
                  <button type="button" onClick={() => { clearComposer(); setIsComposerExpanded(false); }} className="px-4 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-100 rounded-lg">Batal</button>
                  <button type="submit" className="px-4 py-2 text-sm font-semibold text-white bg-green-600 rounded-lg shadow-sm hover:bg-green-700">{editingNoteId ? 'Simpan' : 'Tambah'}</button>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </form>
      </motion.div>

      {/* NOTE GRID */}
      <motion.div className="flex-1 overflow-auto">
        <AnimatePresence>
          {filteredAndSortedNotes.length > 0 ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5">
                {filteredAndSortedNotes.map(note => 
                  <NoteCard 
                    key={note.id} 
                    note={note} 
                    onPin={handleTogglePin}
                    onDelete={handleDeleteNote}
                    onEdit={handleEdit}
                  />
                )}
              </div>
          ) : (
              <div className="text-center text-gray-500 pt-10">
                <p className="font-semibold">{searchTerm ? "Tidak ada catatan yang cocok." : "Belum ada catatan."}</p>
              </div>
          )}
        </AnimatePresence>
      </motion.div>
    </div>
  );
}
